/**
 * Shared constants for the Finish-Time-Entry-New module.
 * Module-level values — never re-created on render.
 */

export const TIME_PENALTIES = [
  "ARB", "ENP", "ESP", "SCP", "ZFP", "RDG",
  "OCS", "DNF", "RET", "DSQ", "BFD", "UFD",
];

// "About to Finish" can hold at most this many slots (filled cards + blank boxes)
export const MAX_WATCHLIST_SLOTS = 6;

// Monotonic insertion-order stamp shared by both the moved-in entries
// (FinishTimeBody.addToWatchlist) and the manually-typed boxes
// (AboutToFinishBoard) so the board can render them in true chronological order.
let _watchSeq = Date.now();
export const nextWatchOrder = () => ++_watchSeq;

// Excel-like Tab navigation fields in the finished grid
export const INTERACTIVE_FIELDS = ["hr", "dn", "del"];

export const pad2 = (v) => String(v ?? "00").padStart(2, "0");

export const clamp = (v, max) => {
  const n = parseInt(v, 10);
  return pad2(isNaN(n) ? 0 : Math.min(n, max));
};

// Static row helpers (module-level — never recreated)
export const hasRowTime = (data) =>
  data?.hr != null && data?.min != null && data?.sec != null;

export const isRowError = (data) => !hasRowTime(data);

export const isUnknownSail = (data) => !data?.boatName;

// Sail-number matcher honouring the SN Alpha toggle (old-screen rule,
// app_components_finish-time.jsx:667-674): alpha shown → exact full match
// only; alpha hidden → compare with letters stripped from BOTH sides.
// Guard: if stripping leaves either side empty (sail has no digits at all)
// the numeric comparison is meaningless — fall back to exact match so that
// all-letter sail numbers like "AUS" / "XYZ" never falsely collide via "".
const normalizedPair = (rowSail, typed, snWithAlpha) => {
  const sail = (rowSail || "").trim().toLowerCase();
  const t = (typed || "").trim().toLowerCase();
  if (!snWithAlpha) {
    const sailNum = sail.replace(/[A-Za-z]/g, "");
    const tNum = t.replace(/[A-Za-z]/g, "");
    // Both sides have digits → compare numerics only
    if (sailNum && tNum) return [sailNum, tNum];
  }
  return [sail, t];
};

export const sailMatches = (rowSail, typed, snWithAlpha) => {
  if (!(typed || "").trim()) return false;
  const [sail, t] = normalizedPair(rowSail, typed, snWithAlpha);
  return sail === t;
};

export const sailPartialMatches = (rowSail, typed, snWithAlpha) => {
  if (!(typed || "").trim()) return false;
  const [sail, t] = normalizedPair(rowSail, typed, snWithAlpha);
  return sail.includes(t);
};

const ROW_STYLE = { marginBottom: "3px" };
export const getRowStyle = () => ROW_STYLE;

// ─── Time validation helpers (single source of truth) ──────────────────────
// Convert h/m/s to total seconds. Coerces null/undefined/strings safely.
export const toSeconds = (h, m, s) =>
  Number(h || 0) * 3600 + Number(m || 0) * 60 + Number(s || 0);


export const computeTimeValidation = (data, fleetDetails) => {
  const hasTime = hasRowTime(data);

  if (!hasTime) {
    return {
      hrError: false,
      minError: false,
      secError: false,
    };
  }

  const hrError =  Number(data.hr) > 23 || !!data.errorHR;
  const minError =  Number(data.min) > 59 || !!data.errorMin;
  const secError = Number(data.sec) > 59 || !!data.errorSec;

  return {
    hrError,
    minError,
    secError,
  };
};
